<template>
  <!-- Filters -->
  <v-sheet>
    <v-progress-circular
      color="primary"
      v-if="vehiclesStore.makesDataLoading"
      indeterminate
      class="mt-8 d-block mx-auto"
    ></v-progress-circular>
    <v-card v-else>
      <v-row class="pa-4">
        <!-- MAKE FILTER-->
        <v-col cols="12" sm="4">
          <v-select
            variant="outlined"
            :items="vehiclesStore.availableMakes.map(item => item.make) ?? []"
            class="px-4"
            placeholder="Select a Make"
            hide-details
            v-model="activeMake"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:title>
                  {{ item.title }}
                  <v-badge
                    inline
                    :content="findMakeCount(item.title) ?? '0'"
                    :color="findMakeCount(item.title) ? 'primary' : ''"
                  >
                  </v-badge>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
        <!-- MODEL FILTER -->
        <v-col cols="12" sm="4">
          <v-select
            hide-details
            variant="outlined"
            placeholder="Select a make to get the list of models"
            v-model="activeModel"
            :disabled="!activeMake"
            :items="
              vehiclesStore.availableMakes.find(
                item => item.make === activeMake,
              )?.models ?? []
            "
            class="px-4"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:title>
                  {{ item.title }}
                  <v-badge
                    inline
                    :content="
                      findModelCount(activeMake ?? '', item.title) ?? '0'
                    "
                    :color="
                      findModelCount(activeMake ?? '', item.title)
                        ? 'primary'
                        : ''
                    "
                  >
                  </v-badge>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
        <!-- TRIM FILTER -->
        <v-col cols="12" sm="4">
          <v-select
            hide-details
            variant="outlined"
            placeholder="Select a model to get the list of trims"
            :disabled="!activeMake"
            :items="availableTrimsForGivenModel"
            v-model="activeTrim"
            class="px-4"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:title>
                  {{ item.title }}
                </template>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
        <!-- ADD BUTTON -->
        <v-btn
          cols="12"
          sm="3"
          color="primary"
          min-width="200px"
          max-width="300px"
          class="mx-auto ml-md-auto mr-md-6 ma-4 px-8"
          :disabled="!activeModel"
          @click="buildFilter(activeMake, activeModel, activeTrim)"
          >ADD</v-btn
        >
      </v-row>
      <!-- TABLE -->
      <FilteredItemsTable :selected-placement="props.selectedPlacement" />
    </v-card>
  </v-sheet>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import { useVehiclesStore } from '@/stores/vehicles'
import { useSettingsStore } from '@/stores/settings'
import FilteredItemsTable from './FilteredItemsTable.vue'
import type { VehicleItem } from '@/types'
import { getAvailableTrimsForModel } from '@/api'

const props = defineProps<{
  selectedPlacement: string
}>()

const emit = defineEmits(['loading-filters'])

const vehiclesStore = useVehiclesStore()
const settingsStore = useSettingsStore()

const activeMake = ref()
const activeModel = ref()
const activeTrim = ref()

const availableTrimsForGivenModel = ref<string[]>([])

onMounted(async () => {
  applyFiltersIfPresent()
})

onBeforeUnmount(() => {
  vehiclesStore.vehiclesData = []
})

const applyFiltersIfPresent = () => {
  if (
    settingsStore.placements.placements[props.selectedPlacement]['filter'] &&
    settingsStore.placements.placements[props.selectedPlacement]['filter']
      .length > 0
  ) {
    settingsStore.placements.placements[props.selectedPlacement][
      'filter'
    ].forEach((item: any) => {
      vehiclesStore.vehiclesData.push({
        make: item.make,
        model: item.model,
        count: item.count ?? 0,
        trim: item.trim,
        // add settings
        settings: {
          hideCountIfZero: item.settings.hideCountIfZero,
          customCountText: item.settings.customCountText,
          customUrl: item.settings.customUrl,
          disableWhenCountIsZero: item.settings.disableWhenCountIsZero,
          customImg: item.settings.customImg,
        },
      })
    })
  }
}

const findMakeCount = (make: string) => {
  return vehiclesStore.makeCounts.find(
    (count: VehicleItem) => count.value === make,
  )?.count
}

const findModelCount = (make: string, model: string) => {
  return vehiclesStore.vehiclesInUserInventory?.find(
    (item: VehicleItem) =>
      item.value.toLowerCase() ===
      make.toLowerCase() + '--' + model.toLowerCase(),
  )?.count
}

const getTrimsData = async () => {
  if (activeModel.value) {
    const trimsData = await vehiclesStore.getAvailableTrims(
      2024,
      activeMake.value!,
      activeModel.value,
    )
    availableTrimsForGivenModel.value = trimsData.trims.map(
      (trim: any) => trim.styleName,
    )
  }
}

const buildFilter = async (make: string, model: string, trim?: string) => {
  resetMakeModelTrimData()

  if (!canAddNewRecord(make, model, trim)) return

  const trimImg = trim ? await getTrimImg(make, model, trim) : ''
  const count = findModelCount(make, model)
  vehiclesStore.vehiclesData.push({
    make: make,
    model: model,
    count: count ?? 0,
    trim: trim,
    trimImg: trimImg,
    settings: {
      hideCountIfZero: false,
      customCountText: '',
      customUrl: '',
      disableWhenCountIsZero: false,
      customImg: '',
    },
  })
}

const canAddNewRecord = (
  make: string,
  model: string,
  trim?: string,
): boolean => {
  // check if vehiclesData already includes that item and do nothing
  const itemAlreadyPresent = vehiclesStore.vehiclesData.find(
    item => item.make === make && item.model === model && item.trim === trim,
  )
  if (itemAlreadyPresent) return false

  const sameMakeAndModelPresent = vehiclesStore.vehiclesData.find(
    item => item.make === make && item.model === model,
  )
  if (sameMakeAndModelPresent) return false

  return true
}

const getTrimImg = async (make: string, model: string, trim: string) => {
  const apiUrl = import.meta.env.VITE_VEHICLE_IMG_URL
  const trims = await getAvailableTrimsForModel(2024, make, model)
  const selectedTrim = trims.trims.find((item: any) => item.styleName === trim)
  const styleId = selectedTrim.styleId || ''
  const image = apiUrl + '?styleId=' + styleId
  return image
}

const resetMakeModelTrimData = () => {
  activeMake.value = null
  activeModel.value = null
  activeTrim.value = null
}
watch(activeModel, () => {
  // get available trims after user selects model, since we know make and model
  getTrimsData()
})

watch(
  vehiclesStore.vehiclesData,
  () => {
    // update settings
    settingsStore.placements.placements[props.selectedPlacement]['filter'] = [
      ...vehiclesStore.vehiclesData,
    ]
  },
  { deep: true },
)
watch(
  () => vehiclesStore.vehicleDataLoading,
  (newValue) => {
    emit('loading-filters', newValue)
  }
)

</script>
